import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box, Typography, Button, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Chip, Alert, CircularProgress,
  LinearProgress, Tooltip, IconButton, Menu, MenuItem, TablePagination,
  TextField, InputAdornment,
} from '@mui/material';
import { Add, MoreVert, Search } from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { contractService, projectService } from '@smartgrow/shared';
import type { Contract } from '@smartgrow/shared';

const STATUS_COLORS: Record<string, 'success' | 'default' | 'warning' | 'error'> = {
  ACTIVE: 'success',
  COMPLETED: 'default',
  SUSPENDED: 'warning',
  TERMINATED: 'error',
};

const fmt = (n?: number) => n == null ? '—' : `₦${Number(n).toLocaleString('en-NG')}`;

const CONTRACT_STATUSES = ['ACTIVE', 'COMPLETED', 'SUSPENDED', 'TERMINATED'] as const;

export const ContractsPage = () => {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const urlProjectId = searchParams.get('projectId') ?? '';
  const projectName = searchParams.get('projectName') ?? '';
  const [menuAnchor, setMenuAnchor] = useState<{ el: HTMLElement; id: string } | null>(null);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('ALL');
  const [selectedProjectId, setSelectedProjectId] = useState(urlProjectId);

  const projectId = selectedProjectId || urlProjectId;

  useEffect(() => {
    const t = setTimeout(() => { setDebouncedSearch(search); setPage(0); }, 400);
    return () => clearTimeout(t);
  }, [search]);

  const { data: projectsData } = useQuery({
    queryKey: ['projects-for-filter'],
    queryFn: () => projectService.getProjects({ size: 100 }),
  });

  const { data, isLoading, isError } = useQuery({
    queryKey: ['contracts', page, projectId, statusFilter, debouncedSearch],
    queryFn: () => contractService.search({
      page, size: 10,
      search: debouncedSearch || undefined,
      status: statusFilter === 'ALL' ? undefined : statusFilter,
      projectId: projectId || undefined,
    }),
  });

  const clearProjectFilter = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('projectId');
    next.delete('projectName');
    setSearchParams(next);
    setSelectedProjectId('');
    setPage(0);
  };

  const handleProjectChange = (value: string) => {
    setSelectedProjectId(value);
    setPage(0);
    if (!value && urlProjectId) clearProjectFilter();
  };

  const contracts: Contract[] = data?.content ?? [];

  const statusMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) => contractService.updateStatus(id, status),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['contracts'] }); setMenuAnchor(null); },
  });

  return (
    <Box>
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" fontWeight="bold">Contracts</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={() => navigate('/dashboard/contracts/new')}>
          New Contract
        </Button>
      </Box>

      {urlProjectId && !selectedProjectId && (
        <Box sx={{ mb: 2 }}>
          <Chip
            label={`Project: ${projectName || urlProjectId}`}
            onDelete={clearProjectFilter}
            color="primary"
            variant="outlined"
          />
        </Box>
      )}

      {isError && <Alert severity="error" sx={{ mb: 2 }}>Failed to load contracts.</Alert>}

      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField select size="small" label="Project" value={projectId}
            onChange={(e) => handleProjectChange(e.target.value)} sx={{ minWidth: 180 }}>
            <MenuItem value="">All Projects</MenuItem>
            {(projectsData?.content ?? []).map((p) => (
              <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
            ))}
          </TextField>
          <TextField
            size="small" placeholder="Search by contract #, project, contractor..."
            value={search} onChange={(e) => setSearch(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><Search /></InputAdornment> }}
            sx={{ flex: 1, minWidth: 220 }}
          />
          <TextField select size="small" label="Status" value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)} sx={{ minWidth: 150 }}>
            <MenuItem value="ALL">All</MenuItem>
            {CONTRACT_STATUSES.map((s) => (
              <MenuItem key={s} value={s}>{s.charAt(0) + s.slice(1).toLowerCase()}</MenuItem>
            ))}
          </TextField>
        </Box>
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>#</TableCell>
              <TableCell>Contract #</TableCell>
              <TableCell>Project</TableCell>
              <TableCell>Contractor</TableCell>
              <TableCell>Value</TableCell>
              <TableCell>Paid</TableCell>
              <TableCell>Progress</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Start Date</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 4 }}><CircularProgress size={24} /></TableCell>
              </TableRow>
            ) : contracts.length ? (
              contracts.map((c: Contract, i: number) => (
                <TableRow
                  key={c.id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/dashboard/contracts/${c.id}`)}
                >
                  <TableCell>{page * 10 + i + 1}</TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">{c.contractNumber}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2"
                      sx={{ '&:hover': { textDecoration: 'underline' } }}
                      onClick={(e) => { e.stopPropagation(); navigate(`/dashboard/projects/${c.projectId}`); }}
                    >
                      {c.projectName}
                    </Typography>
                  </TableCell>
                  <TableCell>{c.contractorCompanyName}</TableCell>
                  <TableCell>{fmt(c.contractValue)}</TableCell>
                  <TableCell>{fmt(c.paidAmount)}</TableCell>
                  <TableCell sx={{ minWidth: 120 }}>
                    <Tooltip title={`${Number(c.paymentProgress ?? 0).toFixed(1)}%`}>
                      <Box>
                        <LinearProgress
                          variant="determinate"
                          value={Math.min(Number(c.paymentProgress ?? 0), 100)}
                          sx={{ height: 6, borderRadius: 3 }}
                        />
                        <Typography variant="caption" color="text.secondary">
                          {Number(c.paymentProgress ?? 0).toFixed(0)}%
                        </Typography>
                      </Box>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Chip label={c.status} color={STATUS_COLORS[c.status] ?? 'default'} size="small" />
                  </TableCell>
                  <TableCell>{c.startDate}</TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <Tooltip title="Change status">
                      <IconButton size="small" onClick={(e) => { e.stopPropagation(); setMenuAnchor({ el: e.currentTarget, id: c.id }); }}>
                        <MoreVert fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={10} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                    No contracts found
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={data?.totalElements ?? 0}
          page={page}
          onPageChange={(_e, newPage) => setPage(newPage)}
          rowsPerPage={10}
          rowsPerPageOptions={[10]}
        />
      </TableContainer>

      <Menu
        anchorEl={menuAnchor?.el}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
      >
        {CONTRACT_STATUSES.map((s) => (
          <MenuItem
            key={s}
            onClick={() => menuAnchor && statusMutation.mutate({ id: menuAnchor.id, status: s })}
          >
            Set {s.charAt(0) + s.slice(1).toLowerCase()}
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};
